import { ColumnProps } from "antd/lib/table";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "antd";
import { Link } from "react-router-dom";
import ScaleContext from "../../../../context/scale/context";
import { IScaleMovement } from "../../../../models/scale/response";
import {
  ScaleMovementAction,
  getScaleMovementAction,
  getScaleMovementActionColor,
  getScaleMovementReason,
} from "../../../../models/scale/enum";
import Table from "../../../general/table-components/table";
import styles from "./styles.module.scss";

interface IProps {}

const ScaleMovementsTable: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { movements, movementsQuery, actions, loading } =
    useContext(ScaleContext);

  const columns: ColumnProps<IScaleMovement>[] = [
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      width: 170,
      render: (value: string) =>
        value ? new Date(value).toLocaleString() : "-",
    },
    {
      title: t("action"),
      dataIndex: "action",
      key: "action",
      align: "center",
      width: 120,
      render: (action: ScaleMovementAction) => (
        <Tag color={getScaleMovementActionColor(action)}>
          {t(getScaleMovementAction(action))}
        </Tag>
      ),
    },
    {
      // Most detaches are a side effect of the product changing, not a button
      // someone pressed, so this column is what answers "why did key 7 change".
      title: t("reason"),
      dataIndex: "reason",
      key: "reason",
      align: "center",
      width: 200,
      render: (reason: any) => t(getScaleMovementReason(reason)),
    },
    {
      title: t("hotkey"),
      dataIndex: "hotkey",
      key: "hotkey",
      align: "center",
      width: 90,
    },
    {
      title: t("from_hotkey"),
      dataIndex: "fromHotkey",
      key: "fromHotkey",
      align: "center",
      width: 110,
      render: (value: number | null) => value ?? "-",
    },
    {
      title: t("product"),
      dataIndex: "product",
      key: "product",
      align: "center",
      width: 280,
      render: (_: any, record) => {
        const productId = record?.product?.id;
        const productName = record?.product?.name ?? "-";

        // Deleted products stay in the log; their page may no longer open.
        if (!productId) return productName;

        return <Link to={`/products/details/${productId}`}>{productName}</Link>;
      },
    },
    {
      title: t("created_by"),
      dataIndex: "createdBy",
      key: "createdBy",
      align: "center",
      width: 180,
      // Empty is the normal case: the server did it, nobody clicked.
      render: (_: any, record) =>
        record?.createdBy
          ? `${record.createdBy.firstName ?? ""} ${
              record.createdBy.lastName ?? ""
            }`.trim() || "-"
          : t("automatic"),
    },
  ];

  return (
    <Table<any>
      rowKey="id"
      showPagination={true}
      columns={columns}
      dataSource={movements?.data ?? []}
      loading={loading.includes("movements") || movements == undefined}
      size="small"
      className={styles.table}
      total={movements?.total}
      pageSize={movementsQuery.perPage}
      page={movementsQuery.page}
      onPaginationChange={(page, pageSize) => {
        actions.setMovementsQuery({
          ...movementsQuery,
          page,
          perPage: pageSize,
        });
      }}
    />
  );
};

export default ScaleMovementsTable;
